import { useCallback } from "react"
import { FlightMapRoute } from "@/types/flights/flight-map-route"

import { useAppDispatch } from "@/lib/storage/hooks"
import { setHoveredFlight } from "@/lib/storage/map-hover-slice"

const HOVER_DELAY_MS = 200

// These timers are shared across every flight card instance so entering one card cancels any pending "clear"
// Without this coordination, hovering from one card to the next dispatches setHoveredFlight(undefined) mid-transition,
// which snaps the map back to its default viewport and produces a flicker.
let enterTimer: ReturnType<typeof setTimeout> | undefined
let leaveTimer: ReturnType<typeof setTimeout> | undefined

interface UseFlightCardHoverArgs {
  slice: FlightMapRoute | undefined
  enabled: boolean
}

/**
 * Mouse enter/leave handlers that highlight a flight's route on the map while hovering its card.
 */
export function useFlightCardHover({ slice, enabled }: UseFlightCardHoverArgs) {
  const dispatch = useAppDispatch()

  const onMouseEnter = useCallback(() => {
    clearTimeout(leaveTimer)
    clearTimeout(enterTimer)
    if (!enabled || !slice) return
    enterTimer = setTimeout(() => {
      dispatch(setHoveredFlight(slice))
    }, HOVER_DELAY_MS)
  }, [dispatch, slice, enabled])

  const onMouseLeave = useCallback(() => {
    clearTimeout(enterTimer)
    if (!enabled) return
    leaveTimer = setTimeout(() => {
      dispatch(setHoveredFlight(undefined))
    }, HOVER_DELAY_MS)
  }, [dispatch, enabled])

  return { onMouseEnter, onMouseLeave }
}
